import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Clock, Mail, Shield } from "lucide-react";

const AwaitingApproval = () => {
  const navigate = useNavigate();
  const { user, isAwaitingApproval, loading } = useAuth();

  useEffect(() => {
    // If auth is still loading, wait
    if (loading) return;
    
    // If user is approved, redirect to dashboard
    if (user && user.isApproved) {
      navigate('/dashboard', { replace: true });
    }
    
    // If user is not awaiting approval and not logged in, redirect to login
    if (!isAwaitingApproval && !user) {
      navigate('/login', { replace: true });
    }
  }, [user, isAwaitingApproval, loading, navigate]);

  // Don't render anything while loading
  if (loading) {
    return null;
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="mx-auto max-w-md w-full">
        <CardHeader className="text-center space-y-4">
          <div className="flex justify-center">
            <div className="rounded-full bg-amber-100 p-3">
              <Clock className="h-8 w-8 text-amber-600" />
            </div>
          </div>
          <CardTitle className="text-2xl">Account Under Review</CardTitle>
          <CardDescription>
            Your email has been verified successfully. Your account is now being reviewed by our administrators.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-4">
            <div className="flex items-start space-x-3 rounded-lg border p-4">
              <Shield className="h-5 w-5 text-blue-500 mt-0.5" />
              <div className="space-y-1">
                <p className="text-sm font-medium">What happens next?</p>
                <p className="text-sm text-muted-foreground">
                  Our team will review your application and verify your credentials. This process typically takes 1-2 business days.
                </p>
              </div>
            </div>
            
            <div className="flex items-start space-x-3 rounded-lg border p-4">
              <Mail className="h-5 w-5 text-green-500 mt-0.5" />
              <div className="space-y-1">
                <p className="text-sm font-medium">You'll be notified</p>
                <p className="text-sm text-muted-foreground">
                  We'll send you an email notification once your account has been approved and you can start using the platform.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <Button 
              onClick={() => navigate('/login')}
              className="w-full"
              variant="outline"
            >
              Return to Login
            </Button>
            
            <div className="text-center">
              <p className="text-xs text-muted-foreground">
                Need help? Contact us at{" "}
                <a 
                  href="mailto:support@emumentoring.com" 
                  className="text-primary hover:underline"
                >
                  support@emumentoring.com
                </a>
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AwaitingApproval;